/**
 * @name Hook: useGetRules
 * @description A custom hook that retrieves rule unit and role state from the Redux store,
 *    and provides functions to get content revisions, refresh the page, and handle target items.
 * @returns An object containing the content revision, invalid access flag, loading state, refresh function, target item and selectedRule.
 */

import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../redux/store";
import {
  clearAllState,
  getContentById,
  getContentList,
  getFavouriteRules,
  getMyAcknowledgement,
  getRulesOfWeek,
  selectRule,
  setSelectedUserLocations,
  setSelectedUserRoles,
} from "../redux/actions";
import { getMyChatThreads } from "../redux/actions/Chat";
import { RuleUnitState } from "../types/models/RuleUnitState";
import { RoleState } from "../types/models/RoleState";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getOrigin } from "../utils/RuleUtil";
import { RuleInstanceTargetItem } from "../pages/rules/ContentRevisionView/RuleInstanceList";

// delay function used to create a delay between API calls
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export function useRules() {
  const params = useParams();
  const navigate = useNavigate();
  const ruleUnitState = useSelector<AppState, RuleUnitState>((state) => state.ruleUnit);
  const { selectedRule, baseContentRevisionDetails, baseSupportDocumentContentRevisionDetails } =
    ruleUnitState;
  const roleState = useSelector<AppState, RoleState>((state) => state.role);
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const [invalidAccess, setInvalidAccess] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [targetItem, setTargetItem] = useState<RuleInstanceTargetItem | undefined>();
  const [searchTargetItem, setSearchTargetItem] = useState<
    { type: "rule" | "module"; id: string } | undefined
  >();

  const getCurrentContentRevision = useCallback(() => {
    const contentRevision = (baseContentRevisionDetails ?? [])
      .concat(baseSupportDocumentContentRevisionDetails ?? [])
      .find((detail) =>
        detail.RuleInstances?.some((rule) => rule.RuleInstanceId === selectedRule?.RuleInstanceId)
      );

    return contentRevision;
  }, [selectedRule, baseContentRevisionDetails, baseSupportDocumentContentRevisionDetails]);

  useEffect(() => {
    const ruleInstanceId = searchParams.get("ruleId");
    const moduleId = searchParams.get("moduleId");

    if (ruleInstanceId) {
      const allContents = [
        ...(baseContentRevisionDetails || []),
        ...(baseSupportDocumentContentRevisionDetails || []),
      ];

      const allRules = allContents
        .flatMap((content) => content.RuleInstances || [])
        .filter(Boolean);

      // Find the rule with matching origin
      const origin = allRules.find((rule) => {
        const originId = getOrigin(rule?.Metadata);
        return originId === ruleInstanceId;
      });

      if (origin) {
        dispatch(selectRule(origin));
        navigate(`/rules/${origin.RuleId}`);
      }

      setSearchTargetItem({ type: "rule", id: ruleInstanceId });
    } else if (moduleId) {
      setSearchTargetItem({ type: "module", id: moduleId });
    } else {
      setSearchTargetItem(undefined);
    }
  }, [
    searchParams,
    baseContentRevisionDetails,
    baseSupportDocumentContentRevisionDetails,
    dispatch,
  ]);

  useEffect(() => {
    const ruleInstanceId = params.ruleId;

    if (!ruleInstanceId) {
      setTargetItem(undefined);
      return;
    }

    if (
      ruleInstanceId &&
      (!baseContentRevisionDetails || baseContentRevisionDetails.length === 0)
    ) {
      setTargetItem({
        type: "rule",
        id: ruleInstanceId,
        name: "",
        contentId: "",
      });
      return;
    }

    const allContentDetails = [
      ...(baseContentRevisionDetails || []),
      ...(baseSupportDocumentContentRevisionDetails || []),
    ];

    const content = allContentDetails
      .map((content) => content.RuleInstances)
      .flat()
      .filter((rule) => rule !== undefined);

    const origin = content?.find(
      (rule) => getOrigin(rule?.Metadata) === ruleInstanceId || rule?.RuleId === ruleInstanceId
    );

    if (origin) {
      dispatch(selectRule(origin));
    }

    // Find which content revision contains this rule
    let contentId = "";
    let ruleName = "";

    if (ruleInstanceId) {
      // Look through all content revisions to find which one contains this rule
      for (const contentRevision of allContentDetails) {
        const foundRule = contentRevision.RuleInstances?.find(
          (rule) => rule.RuleId === ruleInstanceId
        );

        if (foundRule) {
          contentId = contentRevision.ContentRevisionId;
          ruleName = foundRule.DisplayName || "";
          break;
        }
      }

      setTargetItem({
        type: "rule",
        id: ruleInstanceId,
        name: ruleName,
        contentId: contentId,
      });
    }
  }, [params, baseContentRevisionDetails, baseSupportDocumentContentRevisionDetails]);

  useEffect(() => {
    if (targetItem && targetItem.type === "rule" && !targetItem.contentId) {
      const allContentDetails = [
        ...(baseContentRevisionDetails || []),
        ...(baseSupportDocumentContentRevisionDetails || []),
      ];

      if (allContentDetails.length > 0) {
        // Try to find content that contains target rule
        let contentId = "";
        let ruleName = "";

        for (const contentRevision of allContentDetails) {
          const foundRule = contentRevision.RuleInstances?.find(
            (rule) => rule.RuleId === targetItem.id
          );

          if (foundRule) {
            contentId = contentRevision.ContentRevisionId;
            ruleName = foundRule.DisplayName || "";
            break;
          }
        }

        if (contentId) {
          setTargetItem({
            ...targetItem,
            contentId,
            name: ruleName,
          });
        }
      }
    }
  }, [targetItem, baseContentRevisionDetails, baseSupportDocumentContentRevisionDetails]);

  useEffect(() => {
    if (
      ruleUnitState.baseAlertContentRevisions &&
      (!ruleUnitState.baseAlertContentRevisionDetails ||
        ruleUnitState.baseAlertContentRevisionDetails.length === 0)
    ) {
      // Call detail API if it's not called yet
      ruleUnitState.baseAlertContentRevisions.forEach(async (alertContent, index) => {
        if (index > 0) {
          await delay(500);
        }
        dispatch(getContentById(alertContent.ContentRevisionId, null));
      });
    }
  }, [ruleUnitState.baseAlertContentRevisions]);

  useEffect(() => {
    if (selectedRule) {
      // Clear selection if the searched rules does not include selected rule
      if (
        !ruleUnitState.contentRevisionDetails?.find((detail) =>
          detail.RuleInstances?.some((rule) => rule.RuleId === selectedRule.RuleId)
        ) &&
        !ruleUnitState.supportDocumentContentRevisionDetails?.find((detail) =>
          detail.RuleInstances?.some((rule) => rule.RuleId === selectedRule.RuleId)
        )
      ) {
        dispatch(selectRule(undefined));
      }
    }
  }, [
    dispatch,
    ruleUnitState.contentRevisionDetails,
    ruleUnitState.supportDocumentContentRevisionDetails,
    selectedRule,
  ]);

  const getPinnedRules = useCallback(async () => {
    await new Promise<void>((resolve) => {
      dispatch(getFavouriteRules(() => resolve));
    });
  }, [dispatch]);

  const getRuleOfWeekList = useCallback(async () => {
    await new Promise<void>((resolve) => {
      dispatch(getRulesOfWeek(() => resolve));
    });
  }, [dispatch]);

  const getRules = useCallback(async () => {
    try {
      setIsLoading(true);

      // Get acknowledgements first
      const ackResult = await new Promise<boolean>((resolve) => {
        dispatch(getMyAcknowledgement((result) => resolve(result)));
      });

      if (!ackResult) {
        setInvalidAccess(true);
        setIsLoading(false);
        return;
      }

      await delay(500);

      // Get chat threads
      await new Promise<void>((resolve) => {
        dispatch(getMyChatThreads(() => resolve()));
      });

      await delay(500);

      // Get favorites
      await new Promise<void>((resolve) => {
        dispatch(getFavouriteRules(() => resolve()));
      });
    } catch (error) {
      console.error("Error in getRules:", error);
      setInvalidAccess(true);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  const refreshRules = useCallback(async () => {
    try {
      setIsLoading(true);

      // Step 1: Clear state
      dispatch(clearAllState());

      // Step 2: Set up initial state
      dispatch(setSelectedUserRoles(roleState.myRoles?.map((role) => role.RoleId) ?? []));
      dispatch(
        setSelectedUserLocations(roleState.myLocations?.map((location) => location.LNIId) ?? [])
      );

      await delay(500);

      // Step 3: Get content list and wait for it to complete
      await new Promise<void>((resolve) => {
        dispatch(getContentList(() => resolve()));
      });

      await delay(500);

      // Step 4: Get rules and related data
      await getRules();

      // Step 5: Get rule of the weeks
      await getRuleOfWeekList();
    } catch (error) {
      console.error("Error in refreshRules:", error);
      setInvalidAccess(true);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, roleState.myRoles, roleState.myLocations, getRules]);

  return {
    getCurrentContentRevision,
    baseContentRevisionDetails,
    invalidAccess,
    isLoading,
    refreshRules,
    targetItem,
    selectedRule,
    getPinnedRules,
    searchTargetItem,
    getRuleOfWeekList,
  };
}
